import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowLeft, faFileContract} from '@fortawesome/free-solid-svg-icons';
import {useBaseUrl} from '../../../utils/utils';
import DigitalContractViewer from './DigitalContractViewer';

/**
 * DigitalContractViewer.tsx shows exactly one contract given a contractId -
 * it had no host that ever gave it one (zero importers anywhere in the
 * codebase per the frontend audit). This is that host: lists the contracts
 * for a collaboration (digital_contracts_crud already supports filtering by
 * ?collaboration_id=) and hands off to the viewer once one is picked.
 */

type ContractSummary = {
    contract_id: string;
    title: string;
    contract_type: string;
    status: string;
    created_at: string;
    expiry_date: string;
    signatures_count: number;
};

type Props = {
    collaborationId: string;
    onClose: () => void;
};

const ContractsPanel: React.FC<Props> = ({collaborationId, onClose}) => {
    const baseUrl = useBaseUrl();
    const [contracts, setContracts] = useState<ContractSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        setLoading(true);
        axios.get(`${baseUrl}collaborations/digital-contracts/`, {
            params: {collaboration_id: collaborationId},
            headers: {Authorization: token ? `Bearer ${token}` : ''},
        }).then((res) => {
            setContracts(res.data?.data || []);
        }).finally(() => setLoading(false));
    }, [baseUrl, collaborationId]);

    if (selectedContractId) {
        return (
            <DigitalContractViewer
                contractId={selectedContractId}
                onClose={() => setSelectedContractId(null)}
            />
        );
    }

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="mb-0">
                            <FontAwesomeIcon icon={faFileContract} className="me-3 text-primary"/>
                            Contracts
                        </h3>
                        <button className="btn btn-outline-secondary" onClick={onClose}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2"/>
                            Back
                        </button>
                    </div>

                    {loading && <p>Loading contracts…</p>}
                    {!loading && contracts.length === 0 && <p className="text-muted">No contracts for this collaboration yet.</p>}

                    <div className="row">
                        {contracts.map((contract) => (
                            <div key={contract.contract_id} className="col-lg-4 mb-3">
                                <div className="card h-100" style={{cursor: 'pointer'}}
                                     onClick={() => setSelectedContractId(contract.contract_id)}>
                                    <div className="card-body">
                                        <h6 className="card-title">{contract.title}</h6>
                                        <p className="text-muted small mb-1">{contract.contract_type}</p>
                                        <span className="badge bg-soft-primary text-primary">{contract.status}</span>
                                        <p className="small text-muted mt-2 mb-0">{contract.signatures_count} signature(s)</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractsPanel;
